import { divisionMappings, divisionMappingsSimple } from "../Constants";
import { TournamentConfig } from "../services/TournamentService";

export const getDivisionDisplayName = (
    division: string,
    tournamentConfigs?: TournamentConfig[]
): string => {
    if (!division) return "";
    if (division === "ligaen") return "Ligaen";
    const mapped = divisionMappings.get(division);
    if (mapped) return mapped;
    const config = tournamentConfigs?.find(c => c.divisionName === division);
    if (config) return config.displayName;
    return division;
};

export const isKvalDivision = (division: string): boolean => {
    return division.includes("kval");
};

// The player page's liga/kval split. The backend takes the same values as `scope`
// (Utils/DivisionScope.cs); 'all' is sent as no scope at all.
export type DivisionScope = 'all' | 'liga' | 'kval';

export const inDivisionScope = (division: string | undefined, scope: DivisionScope): boolean =>
    scope === 'all' || isKvalDivision(division ?? "") === (scope === 'kval');

// "2 Division B" → "2. Division B"; "Ligaen" unchanged.
const formatDivision = (name: string): string => name.replace(/^(\d) /, "$1. ");

type DivisionTeam = { division?: string; playsInLigaen?: boolean } | null | undefined;

// LFT never surfaces the separate kval tournaments (they're their own competitions).
// A team that qualified into Ligaen (plays_in_ligaen) shows "Ligaen"; a kval team that
// did NOT qualify shows no division at all; everything else its normal division name.
export const getLftDivisionLabel = (team: DivisionTeam): string | undefined => {
    if (!team) return undefined;
    if (team.playsInLigaen) return "Ligaen";
    if (isKvalDivision(team.division ?? "")) return undefined;
    const mapped = divisionMappings.get(team.division ?? "");
    return mapped ? formatDivision(mapped) : undefined;
};

// The 2-word key the LFT division filter chips match on (divisionMappingsSimple values).
// Same rules as the label: Ligaen for qualified teams, "" (unmatchable) for kval.
export const getLftDivisionFilterKey = (team: DivisionTeam): string => {
    if (!team) return "";
    if (team.playsInLigaen) return "Ligaen";
    if (isKvalDivision(team.division ?? "")) return "";
    return divisionMappings.get(team.division ?? "")?.split(" ").slice(0, 2).join(" ") || "";
};

// The short division label the Fantasy pages use: 'ligaen' → "Ligaen", '1div' → "1. Division".
// Fantasy folds the A/B/C/D groups into one division, so it maps through the *simple* table.
export const getSimpleDivisionLabel = (division: string): string =>
    formatDivision(divisionMappingsSimple.get(division) || division);
